import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Grid, 
  Chip,
  Paper,
  Divider
} from '@mui/material';
import { 
  AdminPanelSettings, 
  Store, 
  Dashboard as DashboardIcon,
  AutoAwesome,
  History,
  CreditCard,
  Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FlutterServicesAPI from '../../services/flutterServices';
import { useAuth } from '../../contexts/AuthContext';
import { useMerchantAuth } from '../../contexts/MerchantAuthContext';
import { useMerchantPermissions } from '../../contexts/MerchantPermissionContext';
import { merchantAuthAPI } from '../../services/merchantAPI';

const DualAccessPanel = () => {
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [hasMerchantAccess, setHasMerchantAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);
  useEffect(() => {
    merchantAuthAPI.checkAuthVisibility()
      .then(r => { if (typeof r.data?.show_whatsapp_sms_auth === 'boolean') setShowWhatsAppSmsAuth(r.data.show_whatsapp_sms_auth); })
      .catch(() => {});
  }, []);
  const { user: adminUser } = useAuth();
  const { merchant } = useMerchantAuth();
  const { hasPermission: hasMerchantPermission } = useMerchantPermissions();
  const navigate = useNavigate();

  useEffect(() => {
    checkAccess();
  }, [adminUser, merchant]);

  const checkAccess = async () => {
    setLoading(true);
    
    // Check admin access
    const adminAccess = adminUser && adminUser.id;
    setHasAdminAccess(!!adminAccess);
    
    // Check merchant access
    const merchantAccess = merchant && merchant.id;
    setHasMerchantAccess(!!merchantAccess);
    
    setLoading(false);
  };

  const handleAdminAccess = () => {
    navigate('/glam-admin/dashboard');
  };

  const handleMerchantAccess = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading access options...</Typography>
      </Box>
    );
  }

  if (!hasAdminAccess && !hasMerchantAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">No access permissions found. Please contact your administrator.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
        Welcome to Glammify
      </Typography>
      
      <Typography variant="h6" gutterBottom align="center" color="text.secondary" sx={{ mb: 4 }}>
        You have access to multiple panels. Choose where you'd like to go:
      </Typography>

      <Grid container spacing={3}>
        {/* Admin Panel Access */}
        {hasAdminAccess && (
          <Grid item xs={12} md={hasMerchantAccess ? 6 : 12}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
              onClick={handleAdminAccess}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <AdminPanelSettings 
                  sx={{ 
                    fontSize: 64, 
                    color: 'primary.main', 
                    mb: 2 
                  }} 
                />
                <Typography variant="h5" gutterBottom>
                  Admin Panel
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Manage users, roles, permissions, and system settings
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label="System Management" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ mr: 1, mb: 1 }} 
                  />
                  <Chip 
                    label="User Management" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ mr: 1, mb: 1 }} 
                  />
                  <Chip 
                    label="Role & Permissions" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ mr: 1, mb: 1 }} 
                  />
                </Box>

                <Button 
                  variant="contained" 
                  size="large" 
                  startIcon={<AdminPanelSettings />}
                  sx={{ mt: 2 }}
                >
                  Access Admin Panel
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Merchant Panel Access */}
        {hasMerchantAccess && (
          <Grid item xs={12} md={hasAdminAccess ? 6 : 12}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
              onClick={handleMerchantAccess}
            >
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Store 
                  sx={{ 
                    fontSize: 64, 
                    color: 'secondary.main', 
                    mb: 2 
                  }} 
                />
                <Typography variant="h5" gutterBottom>
                  Merchant Panel
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {`Generate AI content, manage ${FlutterServicesAPI.getCreditLabel().toLowerCase()}, and access merchant features`}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label="AI Generation" 
                    color="secondary" 
                    variant="outlined" 
                    sx={{ mr: 1, mb: 1 }} 
                  />
                  <Chip 
                    label={`${FlutterServicesAPI.getCreditLabel()} management`} 
                    color="secondary" 
                    variant="outlined" 
                    sx={{ mr: 1, mb: 1 }} 
                  />
                  <Chip 
                    label="Generation History" 
                    color="secondary" 
                    variant="outlined" 
                    sx={{ mr: 1, mb: 1 }} 
                  />
                </Box>

                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large" 
                  startIcon={<Store />}
                  sx={{ mt: 2 }}
                >
                  Access Merchant Panel
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Quick Access Section */}
      {(hasAdminAccess || hasMerchantAccess) && (
        <Paper sx={{ mt: 4, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Access
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            {hasAdminAccess && (
              <>
                <Grid item xs={6} sm={3}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<DashboardIcon />}
                    onClick={() => navigate('/glam-admin/dashboard')}
                  >
                    Admin Dashboard
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Person />}
                    onClick={() => navigate('/glam-admin/users')}
                  >
                    User Management
                  </Button>
                </Grid>
              </>
            )}
            
            {hasMerchantAccess && (
              <>
                <Grid item xs={6} sm={3}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<AutoAwesome />}
                    onClick={() => navigate('/generate')}
                  >
                    AI Generation
                  </Button>
                </Grid>
                {showWhatsAppSmsAuth && <Grid item xs={6} sm={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<CreditCard />}
                    onClick={() => navigate('/credits')}
                  >
                    {FlutterServicesAPI.getCreditLabel()}
                  </Button>
                </Grid>}
              </>
            )}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default DualAccessPanel;
